/**
 * Rich text editor for Stacks
 * Supports a rich text mode and a Wordpress-style HTML editor mode
 * Allows certain HTML tags including links with the
 * possibility of adding more tags in the future
 *
 * The editor automatically switches to HTML editor mode if the content
 * contains any not allowed tags, for example iframes
 */
import React from "react";
import {
  CompositeDecorator,
  ContentState,
  EditorState,
  convertFromHTML,
  Modifier,
  RichUtils,
} from "draft-js";
import "draft-js/dist/Draft.css";
import Editor from "@draft-js-plugins/editor";
import addLinkPlugin from "./plugins/addLinkPlugin";
import DOMPurify from "dompurify";
import { stateToHTML } from "draft-js-export-html";

import { instantSave } from "../../helpers.js";
import Link from "./Link";
import Textarea from "./Textarea";
import cssStyles from "./styles.scss";

// This could be set as a prop to this component
const allowedTags = ["b", "i", "em", "strong", "a", "p", "br", "div"];

const sanitize = (html, normalizeForDraftJs = true) => {
  // console.log('html', html);
  let clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags.concat("li"),
    ALLOWED_ATTR: ["href", "target"],
    ALLOW_DATA_ATTR: false,
  });

  if (normalizeForDraftJs) {
    clean = clean
      .replace("&nbsp;", " ")
      .replaceAll("<p><br></p>", "<p>&zwnj;</p>")
      .replaceAll("<p>&nbsp;</p>", "<p>&zwnj;</p>"); // https://github.com/facebook/draft-js/issues/231#issuecomment-653557919

    if (!allowedTags.includes("li")) {
      // convert to <p> to preserve line break
      clean = clean.replace(/(<\s*\/?\s*)li(\s*([^>]*)?\s*>)/gi, "$1p$2");
    }
  }

  // console.log('clean', clean);

  return clean;
};

const testIfHtmlUsesAllowedTags = (html) => {
  const anyTags = DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["href", "allow", "allowfullscreen", "frameborder", "scrolling"],
    ALLOW_DATA_ATTR: true,
  });

  const onlyAllowedTags = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: ["href"],
    ALLOW_DATA_ATTR: false,
  });

  return anyTags === onlyAllowedTags;
};

// attempt to convert all line breaks to <br>, which is easier to edit in the plaintext HTML editor
const normalizeForHtmlEditor = (html) => {
  let output = html
    .replaceAll("<p><br></p>", "")
    .replaceAll("<p></p>", "")
    .replaceAll("<br>", "")
    .replaceAll("\n", "<br>");

  return DOMPurify.sanitize(output, {
    FORBID_TAGS: ["p"],
  })
    .replaceAll("<br>", "\n")
    .replaceAll("<br/>", "\n");
};

const denormalizeForHtmlEditor = (html) => html.replaceAll("\n", "<br>");
const escapeTags = (html) =>
  html.replaceAll("<", "&lt;").replaceAll(">", "&gt;");
const unescapeTags = (html) =>
  html.replaceAll("&lt;", "<").replaceAll("&gt;", ">");

export default class DraftJS extends React.Component {
  constructor(props) {
    super(props);

    const { val } = this.props;
    const decorator = new CompositeDecorator([
      {
        strategy: findLinkEntities,
        component: Link,
      },
    ]);
    const valUnescaped = val ? unescapeTags(val) : "";
    const valSanitized = sanitize(valUnescaped);
    const { protocol, host } = window.location;
    // relative links aren't interpreted properly in all browsers by convertFromHTML
    // so here I create absolute links that will be filtered out later
    const initial = valSanitized
      .replaceAll("<p><br></p>", "<p>&zwnj;</p>")
      .replace(/href=['"]\/([^'"]*)['"]/g, `href="${protocol}//${host}/$1"`);
    const htmlMode = !testIfHtmlUsesAllowedTags(valUnescaped);

    const blocksFromHTML = convertFromHTML(initial);
    const state = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );

    this.state = {
      editorState: EditorState.createWithContent(state, decorator),
      htmlMode,
      htmlModeValue: valUnescaped.replaceAll("<br>", "\n"),
    };

    this.plugins = [addLinkPlugin];
  }

  focus = () => this.refs.editor.focus();

  onChange = (editorState) => {
    const html = stateToHTML(editorState.getCurrentContent() || "");
    const prevHtml = stateToHTML(
      this.state.editorState.getCurrentContent() || ""
    );

    // console.log('html', html);
    // console.log('prevHtml', prevHtml);
    // console.log('onChange Draftjs', );

    if (window.unsavedChanges && html !== prevHtml) {
      // throttle instant updates (wait 400ms)
      window.unsavedChanges(true);
      clearTimeout(window.editableChangeThrottle);
      window.editableChangeThrottle = setTimeout(() => {
        instantSave();
      }, 400);
    }

    const { protocol, host } = window.location;

    // stateToHTML replaces relative links with absolute links -- we need to undo this
    const editorContentHtml = stateToHTML(
      editorState.getCurrentContent()
    ).replaceAll(`href="${protocol}//${host}`, 'href="');

    this.setState({
      editorState,
      editorContentHtml,
    });
  };

  handlePastedText = (text, html) => {
    const { editorState } = this.state;

    let { contentBlocks, entityMap } = convertFromHTML(sanitize(html || text));

    let contentState = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap()
    );

    this.onChange(
      EditorState.push(editorState, contentState, "insert-fragment")
    );

    return "handled";
  };

  handleKeyCommand = (command) => {
    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      this.onChange(newState);
      return true;
    }

    return false;
  };

  handleHtmlModeClick = () => {
    this.setState((prevState) => {
      const htmlModeValue = normalizeForHtmlEditor(prevState.editorContentHtml);

      return {
        htmlMode: true,
        htmlModeValue,
      };
    });
  };

  handleHtmlModeChange = (htmlModeValue) => {
    this.setState({ htmlModeValue });

    if (window.unsavedChanges) {
      // throttle instant updates (wait 400ms)
      window.unsavedChanges(true);
      clearTimeout(window.editableChangeThrottle);
      window.editableChangeThrottle = setTimeout(() => {
        instantSave();
      }, 400);
    }
  };

  render() {
    const { editorState, editorContentHtml, htmlMode, htmlModeValue } =
      this.state;
    const value = htmlMode ? htmlModeValue : editorContentHtml || "";
    let valueEscaped = escapeTags(value);

    if (htmlMode) valueEscaped = denormalizeForHtmlEditor(valueEscaped);

    return (
      <div className={cssStyles.root}>
        <div
          className={cssStyles.editor}
          // onClick={this.focus}
        >
          <input
            type="hidden"
            name={this.props.fieldName}
            value={valueEscaped}
          />
          <div style={{ display: htmlMode ? "none" : "" }}>
            <Editor
              editorState={editorState}
              onChange={this.onChange}
              plugins={this.plugins}
              handlePastedText={this.handlePastedText}
              handleKeyCommand={this.handleKeyCommand}
              ref="editor"
            />
          </div>
          {!htmlMode && (
            <button
              style={styles.button}
              className={cssStyles.button}
              type="button"
              onClick={this.handleHtmlModeClick}
            >
              HTML editor 💾
            </button>
          )}

          {!!htmlMode && (
            <Textarea value={value} onUpdate={this.handleHtmlModeChange} />
          )}
        </div>
        {/* { !htmlMode && (
          <div
            style={styles.preview}
          >
            {this.state.editorContentHtml}
          </div>
        )} */}
      </div>
    );
  }
}

function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
}

const styles = {
  preview: {
    whiteSpace: "pre-wrap",
    fontFamily: "Courier New,Courier,monospace",
    fontSize: "15px",
    margin: "10px 0",
    padding: "0 12px",
    maxHeight: "160px",
    overflowY: "auto",
  },
};
